import { useEffect, useState } from "react"
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { fetchMessagePreview } from "~/apis/bookings"
import { createMessageLog } from "~/apis/messageLogs"
import { messageLink } from "~/lib/messaging"
import { PageSpinner } from "~/components/Spinner"
import routes from "~/routes"

const KIND_LABELS = {
  checkin_instructions: "Check-in info — paste into Riparide",
  welcome: "Welcome message",
  checkout: "Check-out note",
}

export default function SendMessage() {
  const { id } = useParams()
  const [ search ] = useSearchParams()
  const kind = search.get("kind") || "welcome"
  const navigate = useNavigate()

  const [ preview, setPreview ] = useState(null)
  const [ body, setBody ] = useState("")
  const [ error, setError ] = useState(null)
  const [ copied, setCopied ] = useState(false)
  const [ busy, setBusy ] = useState(false)

  useEffect(() => {
    fetchMessagePreview(id, kind)
      .then((p) => {
        setPreview(p)
        setBody(p.body)
      })
      .catch((err) => setError(err.message))
  }, [ id, kind ])

  if (error) return <p className="p-8 text-destructive">{error}</p>
  if (!preview) return <PageSpinner />

  const viaRiparide = preview.via === "riparide"
  const smsHref = preview.phone && !viaRiparide ? messageLink(preview.phone, body, "sms") : null
  const waHref = preview.phone && !viaRiparide ? messageLink(preview.phone, body, "whatsapp") : null

  async function copy() {
    await navigator.clipboard.writeText(body)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  async function markSent() {
    setBusy(true)
    try {
      await createMessageLog({ bookingId: Number(id), kind, phone: preview.phone, body })
      navigate(routes.notifications)
    } catch (err) {
      setError(err.message)
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto mt-12 max-w-lg px-6">
      <Link to={routes.notifications} className="mb-3 inline-block text-sm text-muted-foreground">← Back to inbox</Link>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{KIND_LABELS[kind]}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {viaRiparide
              ? "Send this through the Riparide conversation with the guest — it's your standing check-in info from Settings."
              : <>To: <span className="font-medium text-foreground">{preview.phone || "no phone number on the booking"}</span></>}
          </p>
        </CardHeader>
        <CardContent>
          {preview.already_sent && (
            <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              You've already logged this message as sent once.
            </p>
          )}

          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={Math.max(6, body.split("\n").length + 1)}
            className="mb-4"
          />

          {viaRiparide && !body.trim() && (
            <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              No check-in info saved for this property yet — add it under Settings → Properties.
            </p>
          )}

          {viaRiparide ? null : smsHref ? (
            <div className="mb-2 flex gap-2">
              <Button asChild className="flex-1">
                <a href={smsHref}>Open in Messages</a>
              </Button>
              <Button asChild variant="secondary" className="flex-1">
                <a href={waHref} target="_blank" rel="noreferrer">WhatsApp</a>
              </Button>
            </div>
          ) : (
            <p className="mb-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              No phone number on this booking — copy the text below into the platform's chat instead.
            </p>
          )}

          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={copy}>
              {copied ? "Copied ✓" : "Copy message"}
            </Button>
            <Button className="flex-1" onClick={markSent} disabled={busy}>
              Mark as sent
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {viaRiparide
              ? "Copy the text, paste it into Riparide, then tap \"Mark as sent\" so it's logged and the reminder is cleared."
              : "\"Open in Messages\" pre-fills the text — hit send there, come back, then tap \"Mark as sent\" so it's logged and the reminder is cleared."}
          </p>
        </CardContent>
      </Card>
    </div>
  )
}
